import React from 'react';
/**
 * Info Overlay - System Context
 * Shows how project fits into the bigger picture
 */
import { motion } from 'framer-motion';
import { X, Link2 } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { Project } from './data/projects';
interface InfoOverlayProps {
  project: Project;
  onClose: () => void;
}
export function InfoOverlay({
  project,
  onClose
}: InfoOverlayProps) {
  const {
    theme: currentTheme
  } = useTheme();
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`absolute inset-0 z-50 flex items-center justify-center p-3 md:p-4 backdrop-blur-xl ${isLightTheme ? 'bg-gray-900/80' : isHighContrast ? 'bg-white/90' : 'bg-black/80'}`} onClick={onClose} data-id="element-331">
      <motion.div initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.9,
      opacity: 0
    }} onClick={e => e.stopPropagation()} className={`backdrop-blur-2xl rounded-2xl md:rounded-3xl p-5 md:p-8 border max-w-2xl w-full relative max-h-[90vh] overflow-y-auto ${isLightTheme ? 'bg-white/95 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/95 border-fuchsia-500/50' : 'bg-white/10 border-white/20'}`} data-id="element-332">
        {/* Close Button */}
        <button onClick={onClose} className={`absolute top-3 right-3 md:top-4 md:right-4 p-2 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : isHighContrast ? 'bg-gray-100 hover:bg-gray-200' : isNeonTheme ? 'bg-fuchsia-900/20 hover:bg-fuchsia-900/30 border border-fuchsia-500/30' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-333">
          <X className={`w-4 h-4 md:w-5 md:h-5 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-334" />
        </button>
        {/* Content */}
        <div className="space-y-4 md:space-y-6" data-id="element-335">
          {/* Header */}
          <div data-id="element-336">
            <div className={`flex items-center gap-2 text-xs md:text-sm font-medium mb-2 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-fuchsia-400'}`} data-id="element-337">
              <Link2 className="w-3 h-3 md:w-4 md:h-4" data-id="element-338" />
              <span data-id="element-339">Systeem Context</span>
            </div>
            <h2 className={`text-xl md:text-3xl font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-340">
              {project.title}
            </h2>
          </div>
          {/* Pillar Connection */}
          <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-fuchsia-900/20 border-fuchsia-500/30' : 'bg-white/5 border-white/10'}`} data-id="element-341">
            <div className={`text-xs md:text-sm font-bold uppercase mb-2 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-342">
              Pijler
            </div>
            <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-gradient-to-r ${project.gradient} text-white text-sm md:text-base font-bold mb-3 md:mb-4 ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-343">
              {project.systemContext.pillar}
            </div>
            <p className={`leading-relaxed text-sm md:text-base ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-344">
              {project.systemContext.connection}
            </p>
          </div>
          {/* How It Fits */}
          <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-fuchsia-900/20 border-fuchsia-500/30' : 'bg-white/5 border-white/10'}`} data-id="element-345">
            <div className={`text-xs md:text-sm font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-346">
              Hoe Het Past
            </div>
            <p className={`leading-relaxed text-xs md:text-sm ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/70'}`} data-id="element-347">
              Dit project is onderdeel van mijn grotere ecosysteem — het
              demonstreert niet alleen een skill, maar een manier van denken.
              Het laat zien hoe ik complexe vraagstukken vertaal naar werkende
              oplossingen, met aandacht voor zowel technologie als menselijke
              ervaring.
            </p>
          </div>
          {/* Related Projects */}
          <div data-id="element-348">
            <div className={`text-xs md:text-sm font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-349">
              Gerelateerde Projecten
            </div>
            <div className="flex gap-2 flex-wrap" data-id="element-350">
              {['Studio', 'Expertise', 'Process'].map(related => <button key={related} className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors border touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-200' : isHighContrast ? 'bg-gray-100 hover:bg-gray-200 text-black border-black' : isNeonTheme ? 'bg-fuchsia-900/20 hover:bg-fuchsia-900/30 text-cyan-300 border-cyan-500/30' : 'bg-white/5 hover:bg-white/10 text-white/80 border-white/10'}`} data-id="element-351">
                  {related}
                </button>)}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}